import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { Socket } from 'phoenix-socket'
import flatten from 'lodash-es/flatten'

import { reportAutoSuggest } from '../../../common/analytics/searchBarAnalytics'
import autoSuggestEndpoint from '../../../common/constants/autoSuggestEndpoints'
import { WebSocketError, logError } from '../../../common/utils/errors'
import SearchBarForm from '../searchBar/searchBarForm'
import { getTopCategoryLabel } from '../../../config/sites/categories'
import RecentSearch from './recentSearch'
import pushHistory from '../../../common/utils/location/pushHistory'
import redirectSearchIfItemId from '../../../common/utils/location/redirectSearchIfItemId'

import { securitySanitizeString } from '../../../common/utils/location/sanitize'

export default AutoSuggest => {
  class AutoSuggestDataContainer extends React.Component {
    static propTypes = {
      inputName: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      locationParams: PropTypes.object.isRequired,
      makeSearchLink: PropTypes.func.isRequired
    }

    static defaultProps = {
      placeholder: ''
    }

    constructor(props) {
      super(props)
      this.state = {
        suggestions: [],
        inputValue: props.value || ''
      }

      this.socketConnection = null
      this.socketChannel = null

      this.setSuggestions = this.setSuggestions.bind(this)
      this.onInputChange = this.onInputChange.bind(this)
      this.onSelectOption = this.onSelectOption.bind(this)
      this.onInputFocus = this.onInputFocus.bind(this)
    }

    componentDidMount() {
      this.initSocket()
    }

    componentWillUnmount() {
      this.closeSocket()
    }

    componentWillReceiveProps(nextProps) {
      if (nextProps.value !== this.props.value) {
        this.setState({
          inputValue: nextProps.value
        })
      }
    }

    initSocket() {
      const { endpoint, channel } = autoSuggestEndpoint

      this.socketConnection = new Socket(endpoint)
      this.socketChannel = this.socketConnection.channel(channel, {})

      this.socketConnection.connect()

      this.socketChannel.on('new_msg', message => {
        this.setSuggestions(message.body)
      })

      this.socketChannel
        .join()
        .receive('ok', () => {
          if (this.state.inputValue) {
            this.sendMessage(this.state.inputValue)
          }
        })
        .receive('error', reason => {
          logError(WebSocketError({ message: reason }))
        })
    }

    closeSocket() {
      if (this.socketConnection) {
        this.socketConnection.disconnect()
      }
    }

    sendMessage(value = '') {
      const { currentSite } = this.props

      // trim spaces from terms before sending
      // to ES to prevent serverside errors
      const searchString = securitySanitizeString(value).toLowerCase()

      const message = {
        body: searchString,
        category: null,
        site: currentSite || 'themeforest'
      }

      if (
        this.socketConnection &&
        this.socketChannel &&
        this.socketConnection.isConnected() &&
        this.socketChannel.isJoined()
      ) {
        this.socketChannel.push('new_msg', message).receive('error', reason => {
          logError(WebSocketError({ message: reason }))
        })
      }
    }

    setSuggestions(suggestions) {
      this.setState({
        suggestions
      })
    }

    onInputChange(value = '') {
      const { makeSearchLink } = this.props
      this.setState({
        inputValue: value
      })
      if (value === '') {
        this.setSuggestions(RecentSearch.getRecentSearch({}, makeSearchLink))
      } else {
        this.sendMessage(value)
      }
    }

    getLink(term) {
      const { locationParams, makeSearchLink } = this.props

      let nextQueries = Object.assign({}, locationParams, {
        term
      })
      // reset page number
      delete nextQueries.page
      return makeSearchLink(nextQueries)
    }

    getCategoryLink(category, term) {
      const { locationParams, makeSearchLink } = this.props

      let nextQueries = Object.assign({}, locationParams, {
        category,
        term
      })
      // reset page number
      delete nextQueries.page
      return makeSearchLink(nextQueries)
    }

    async onSelectOption(option, event) {
      const { history } = this.props
      const { suggestions } = this.state

      let nextOption

      // if the selection option is a string, we need to build the link
      if (typeof option === 'string') {
        if (redirectSearchIfItemId(option)) {
          return
        }
        nextOption = {
          text: option,
          link: this.getLink(option)
        }
        // if the selection option is an object, it means it's triggered by auto-suggest
        // we need to report this to ga
      } else if (typeof option === 'object') {
        if (redirectSearchIfItemId(option.text)) {
          return
        }
        nextOption = Object.assign({}, option)
        reportAutoSuggest({
          action: event && event.type === 'mousedown' ? 'click' : 'keyboard',
          label: option.text,
          suggestedTerm: suggestions.map(suggestion => {
            return suggestion.text
          })
        })
      }

      if (nextOption && typeof nextOption.text === 'string') {
        if (nextOption.text !== '') {
          RecentSearch.addItem(nextOption.text)
        }

        this.setState(
          {
            inputValue: nextOption.text
          },
          () => {
            pushHistory(history, nextOption.link)
          }
        )
      }
    }

    onInputFocus(value = '') {
      const { locationParams, makeSearchLink } = this.props
      if (value === '') {
        this.setState({
          suggestions: RecentSearch.getRecentSearch(
            locationParams,
            makeSearchLink
          )
        })
      }
    }

    getCategorySuggestions() {
      const { suggestions } = this.state
      const { currentSite } = this.props

      const categorySuggestions = suggestions
        .filter(suggestion => suggestion.type === 'c')
        .map(suggestion => {
          return suggestion.categories.map(categoryKey => ({
            link: this.getCategoryLink(categoryKey, suggestion.text),
            type: suggestion.type,
            text: suggestion.text,
            category:
              getTopCategoryLabel(currentSite, categoryKey) || categoryKey
          }))
        })

      return flatten(categorySuggestions)
    }

    getSuggestions() {
      const { showCategories } = this.props
      const { suggestions } = this.state

      const nonCategorySuggestions = suggestions
        .filter(suggestion => suggestion.type !== 'c')
        .map(suggestion => {
          suggestion.link = this.getLink(suggestion.text)
          return suggestion
        })

      if (showCategories) {
        return this.getCategorySuggestions().concat(nonCategorySuggestions)
      }

      return nonCategorySuggestions
    }

    render() {
      const { inputValue } = this.state
      const { locationParams, makeSearchLink } = this.props

      const autosuggestProps = {
        ...this.props,
        suggestions: this.getSuggestions(),
        onInputChange: this.onInputChange,
        onSelectOption: this.onSelectOption,
        inputValue,
        type: 'search',
        onInputFocus: this.onInputFocus
      }
      return (
        <SearchBarForm
          locationParams={locationParams}
          makeSearchLink={makeSearchLink}
        >
          <AutoSuggest {...autosuggestProps} />
        </SearchBarForm>
      )
    }
  }

  return withRouter(AutoSuggestDataContainer)
}
